import toast from 'react-hot-toast';
import Papa from 'papaparse';
import { fetchPersonsData } from 'api/prediction-engine';
import { LOAD_TABLE_FIELDS } from '../constants/table_fields';
import { TableRow } from '../types';
import { Person } from '../../../types/prediction-engine';

const DEFAULT_BATCH_SIZE = 500;

const fetchPersonsDataInBatches = async (
  personIds: string[],
  batchSize: number = DEFAULT_BATCH_SIZE,
): Promise<{ [key: string]: Person }> => {
  const batches: string[][] = [];
  for (let i = 0; i < personIds.length; i += batchSize) {
    batches.push(personIds.slice(i, i + batchSize));
  }

  const results = await Promise.all(
    batches.map((batch) => fetchPersonsData({ personsIds: batch })),
  );

  return results.reduce((merged, batchResult) => {
    return { ...merged, ...batchResult };
  }, {});
};

export const parseTabSeparatedRows = (data: string): string[][] => {
  const uuidPattern =
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/;
  const headerPattern = /^Internal ID\t/;

  const lines = data.split('\n');
  const rows: string[] = [];
  let currentRow = '';

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (uuidPattern.test(line.trim()) || headerPattern.test(line)) {
      if (currentRow.trim()) {
        rows.push(currentRow.trim());
      }
      currentRow = line;
    } else if (line.trim()) {
      currentRow += ' ' + line.trim();
    }
  }

  if (currentRow.trim()) {
    rows.push(currentRow.trim());
  }

  return rows.map((row) => row.split('\t').map((cell) => cell.trim()));
};

export const parseDelimitedLine = (line: string): string[] => {
  if (line.includes('\t') && !line.includes(',')) {
    return line.split('\t').map((cell) => cell.trim());
  }

  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim().replace(/^"(.*)"$/, '$1'));
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim().replace(/^"(.*)"$/, '$1'));
  return result;
};

export const parseCSVRows = (data: string): string[][] => {
  // Remove BOM if present
  const cleanedData = data.replace(/^\uFEFF/, '');

  const result = Papa.parse<string[]>(cleanedData, {
    delimiter: ',',
    quoteChar: '"',
    escapeChar: '"',
    skipEmptyLines: true,
  });

  if (result.errors && result.errors.length > 0) {
    console.warn('CSV parsing errors:', result.errors);
  }

  return result.data || [];
};

export const getColumnKeyFromHeader = (header: string): string | null => {
  const headerMappings: { [key: string]: string } = {
    'Internal ID': LOAD_TABLE_FIELDS.internal_id,
    'Version ID': LOAD_TABLE_FIELDS.version_id,
    Title: LOAD_TABLE_FIELDS.title,
    'Release Date': LOAD_TABLE_FIELDS.release_date,
    Budget: LOAD_TABLE_FIELDS.budget,
    'Production Budget': LOAD_TABLE_FIELDS.budget,
    Actors: LOAD_TABLE_FIELDS.actors,
    'Actors IDs': 'actors_ids',
    Directors: LOAD_TABLE_FIELDS.directors,
    'Directors IDs': 'directors_ids',
    Writers: LOAD_TABLE_FIELDS.writers,
    'Writers IDs': 'writers_ids',
    Producers: LOAD_TABLE_FIELDS.producers,
    'Producers IDs': 'producers_ids',
    Stars: LOAD_TABLE_FIELDS.stars,
    'Stars IDs': 'stars_ids',
    Plot: LOAD_TABLE_FIELDS.plot,
    Script: 'script_id',
    'MPAA Rating': LOAD_TABLE_FIELDS.mpaa_rating,
    Runtime: LOAD_TABLE_FIELDS.runtime,
    'Aspect Ratio': LOAD_TABLE_FIELDS.aspect_ratio,
    Genres: LOAD_TABLE_FIELDS.genres,
  };

  return headerMappings[header] || null;
};

export const createEmptyRow = (): TableRow => ({
  [LOAD_TABLE_FIELDS.internal_id]: '',
  [LOAD_TABLE_FIELDS.version_id]: '',
  [LOAD_TABLE_FIELDS.pred_roi]: '',
  [LOAD_TABLE_FIELDS.score]: '',
  [LOAD_TABLE_FIELDS.title]: '',
  [LOAD_TABLE_FIELDS.release_date]: '',
  [LOAD_TABLE_FIELDS.budget]: '',
  [LOAD_TABLE_FIELDS.actors]: [],
  [LOAD_TABLE_FIELDS.directors]: [],
  [LOAD_TABLE_FIELDS.writers]: [],
  [LOAD_TABLE_FIELDS.producers]: [],
  [LOAD_TABLE_FIELDS.stars]: [],
  [LOAD_TABLE_FIELDS.plot]: '',
  [LOAD_TABLE_FIELDS.mpaa_rating]: '',
  [LOAD_TABLE_FIELDS.runtime]: '',
  [LOAD_TABLE_FIELDS.aspect_ratio]: '',
  [LOAD_TABLE_FIELDS.genres]: [],
});

export const isUUID = (value: string): boolean => {
  const uuidPattern =
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
  return uuidPattern.test(value.trim());
};

export const detectPasteType = (clipboardData: string) => {
  // Remove BOM if present
  const cleanedData = clipboardData.replace(/^\uFEFF/, '');

  const lines = cleanedData
    .trim()
    .split('\n')
    .filter((line) => line.trim());

  // Count tabs and commas in first line to determine format
  const firstLine = lines[0] || '';
  const tabCount = (firstLine.match(/\t/g) || []).length;
  const commaCount = (firstLine.match(/,/g) || []).length;

  // CSV files typically have more commas than tabs
  // Tab-separated data from Excel paste has only tabs
  const hasTabSeparatedData = tabCount > 0 && commaCount === 0;
  const hasCommaSeparatedData = commaCount > 0;

  const hasMultipleColumns = hasTabSeparatedData || hasCommaSeparatedData;
  const hasMultipleRows = lines.length > 1;
  const hasHeaders =
    hasMultipleColumns &&
    lines[0] &&
    (lines[0].includes('Internal ID') ||
      lines[0].includes('Title') ||
      lines[0].includes('Release Date'));

  // Check if all non-empty lines are UUIDs (film IDs)
  const isUUIDs = lines.length > 0 && lines.every((line) => isUUID(line));

  return {
    cleanedData,
    lines,
    hasMultipleColumns,
    hasMultipleRows,
    hasHeaders,
    hasTabSeparatedData,
    hasCommaSeparatedData,
    isUUIDs,
  };
};

// Person and genre columns hold lists picked through their search box; a
// pasted string would replace the list and crash the cell.
const isListColumn = (columnKey: string): boolean =>
  Array.isArray(createEmptyRow()[columnKey as keyof TableRow]);

const LIST_COLUMN_PASTE_ERROR = 'Open the field and paste into its search box';

export const handleSingleCellPaste = async (
  value: string,
  rowIndex: number,
  columnKey: string,
  tableData: TableRow[],
  setTableData: (data: TableRow[]) => void,
) => {
  if (isListColumn(columnKey)) {
    toast.error(LIST_COLUMN_PASTE_ERROR);
    return;
  }

  const newTableData = [...tableData];

  while (newTableData.length <= rowIndex) {
    newTableData.push(createEmptyRow());
  }

  newTableData[rowIndex][columnKey as keyof TableRow] = value.trim();
  newTableData[rowIndex].is_export_allowed = true;
  setTableData(newTableData);
  toast.success('Pasted single value');
};

export const handleSingleColumnPaste = async (
  values: string[],
  startRowIndex: number,
  columnKey: string,
  tableData: TableRow[],
  setTableData: (data: TableRow[]) => void,
) => {
  if (isListColumn(columnKey)) {
    toast.error(LIST_COLUMN_PASTE_ERROR);
    return;
  }

  const newTableData = [...tableData];

  for (let i = 0; i < values.length; i++) {
    const rowIndex = startRowIndex + i;

    while (newTableData.length <= rowIndex) {
      newTableData.push(createEmptyRow());
    }

    newTableData[rowIndex][columnKey as keyof TableRow] = values[i].trim();
    newTableData[rowIndex].is_export_allowed = true;
  }

  setTableData(newTableData);
  toast.success(`Pasted ${values.length} values into ${columnKey}`);
};

const isHeaderRow = (line: string): boolean => {
  const headerIndicators = [
    'Internal ID',
    'Version ID',
    'Title',
    'Release Date',
    'Budget',
    'Actors',
    'Directors',
    'Writers',
    'Producers',
    'Stars',
    'Plot',
    'MPAA Rating',
    'Runtime',
    'Aspect Ratio',
    'Genres',
  ];

  const firstPart = line.substring(0, 300);
  const headerCount = headerIndicators.filter((header) =>
    firstPart.includes(header),
  ).length;

  const startsWithInternalId = line.trim().startsWith('Internal ID');

  const uuidPattern =
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/i;
  const containsUUID = uuidPattern.test(line);

  return (startsWithInternalId || headerCount >= 3) && !containsUUID;
};

const hasScriptIdInNextColumn = (
  values: string[],
  currentColIndex: number,
  standardColumnOrder: string[],
): boolean => {
  const nextColIndex = currentColIndex + 1;
  if (
    nextColIndex >= values.length ||
    nextColIndex >= standardColumnOrder.length
  ) {
    return false;
  }
  const nextColumnKey = standardColumnOrder[nextColIndex];
  return nextColumnKey === 'script_id' && Boolean(values[nextColIndex]?.trim());
};

const processPersonIds = (value: string, columnKey: string): string[] => {
  const personType = columnKey.replace('_ids', '');
  return value
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id);
};

const processGenres = (value: string): string[] => {
  return value
    .split(',')
    .map((genre) => genre.trim())
    .filter((genre) => genre);
};

const parseMoneyValue = (value: string): string => {
  const cleaned = value.replace(/[$,\s]/g, '');
  return cleaned !== '' && !isNaN(Number(cleaned)) ? cleaned : value;
};

const setScriptData = (row: TableRow, scriptId: string, scriptName: string) => {
  row.script = { id: scriptId, name: scriptName || '' };
  if (scriptName) {
    row[LOAD_TABLE_FIELDS.plot] = scriptName;
  }
};

const findScriptIdColumnIndex = (columnMap: {
  [key: number]: string;
}): number | null => {
  const entry = Object.entries(columnMap).find(
    ([_, key]) => key === 'script_id',
  );
  return entry ? parseInt(entry[0]) : null;
};

export const handleTabDataPaste = async (
  tabData: string,
  startRowIndex: number = 0,
  tableData: TableRow[],
  setTableData: (data: TableRow[]) => void,
) => {
  try {
    const parsedRows = parseTabSeparatedRows(tabData);

    if (parsedRows.length === 0) {
      toast.error('No data found to paste');
      return;
    }

    // console.log('Parsed rows:', parsedRows.length);

    const hasHeaders =
      parsedRows.length > 0 && isHeaderRow(parsedRows[0].join('\t'));
    const dataRows = hasHeaders ? parsedRows.slice(1) : parsedRows;

    // console.log('Has headers:', hasHeaders, 'Data rows:', dataRows.length);

    if (dataRows.length === 0) {
      toast.error('No data rows found after filtering headers');
      return;
    }

    const newTableData = [...tableData];
    const allPersonIds: string[] = [];
    const rowPersonIds: {
      [rowIndex: number]: { [personType: string]: string[] };
    } = {};

    const standardColumnOrder = [
      LOAD_TABLE_FIELDS.internal_id,
      LOAD_TABLE_FIELDS.version_id,
      LOAD_TABLE_FIELDS.title,
      LOAD_TABLE_FIELDS.release_date,
      LOAD_TABLE_FIELDS.budget,
      LOAD_TABLE_FIELDS.actors,
      'actors_ids',
      LOAD_TABLE_FIELDS.directors,
      'directors_ids',
      LOAD_TABLE_FIELDS.writers,
      'writers_ids',
      LOAD_TABLE_FIELDS.producers,
      'producers_ids',
      LOAD_TABLE_FIELDS.stars,
      'stars_ids',
      LOAD_TABLE_FIELDS.plot,
      'script_id',
      LOAD_TABLE_FIELDS.mpaa_rating,
      LOAD_TABLE_FIELDS.runtime,
      LOAD_TABLE_FIELDS.aspect_ratio,
      LOAD_TABLE_FIELDS.genres,
    ];

    for (let i = 0; i < dataRows.length; i++) {
      const values = dataRows[i];
      const rowIndex = startRowIndex + i;

      while (newTableData.length <= rowIndex) {
        newTableData.push(createEmptyRow());
      }

      const personIds: { [personType: string]: string[] } = {};
      let scriptId: string | null = null;
      let scriptName: string | null = null;

      values.forEach((value, colIndex) => {
        if (colIndex >= standardColumnOrder.length) return;

        const columnKey = standardColumnOrder[colIndex];
        if (!columnKey || !value) return;

        // Handle person IDs (actors_ids, directors_ids, etc.)
        if (columnKey.endsWith('_ids')) {
          const personType = columnKey.replace('_ids', '');
          personIds[personType] = processPersonIds(value, columnKey);
          return;
        }

        // Handle script ID
        if (columnKey === 'script_id') {
          scriptId = value.trim();
          return;
        }

        // Handle plot/script name
        if (columnKey === LOAD_TABLE_FIELDS.plot) {
          if (hasScriptIdInNextColumn(values, colIndex, standardColumnOrder)) {
            scriptName = value.trim();
          } else {
            newTableData[rowIndex][columnKey as keyof TableRow] = value;
          }
          return;
        }

        // Handle genres
        if (columnKey === LOAD_TABLE_FIELDS.genres) {
          newTableData[rowIndex][columnKey as keyof TableRow] =
            processGenres(value);
          return;
        }

        // Handle budget
        if (columnKey === LOAD_TABLE_FIELDS.budget) {
          newTableData[rowIndex][columnKey as keyof TableRow] =
            parseMoneyValue(value);
          return;
        }

        // Handle regular fields
        newTableData[rowIndex][columnKey as keyof TableRow] = value;
      });

      // Build script object if we have script_id
      if (scriptId) {
        setScriptData(newTableData[rowIndex], scriptId, scriptName || '');
      }

      // Mark row as exportable since it was imported
      newTableData[rowIndex].is_export_allowed = true;

      if (Object.keys(personIds).length > 0) {
        rowPersonIds[rowIndex] = personIds;
        Object.values(personIds).forEach((ids) => allPersonIds.push(...ids));
      }
    }

    if (allPersonIds.length > 0) {
      const uniquePersonIds = [...new Set(allPersonIds)];
      const personsData = await fetchPersonsDataInBatches(uniquePersonIds);

      Object.entries(rowPersonIds).forEach(([rowIndexStr, personTypes]) => {
        const rowIndex = parseInt(rowIndexStr);

        Object.entries(personTypes).forEach(([personType, ids]) => {
          const persons = ids.map((id) => personsData[id]).filter(Boolean);
          const personKey =
            LOAD_TABLE_FIELDS[personType as keyof typeof LOAD_TABLE_FIELDS];

          if (personKey) {
            newTableData[rowIndex][personKey as keyof TableRow] = persons;
          }
        });
      });
    }

    setTableData(newTableData);
    toast.success(
      `Successfully pasted ${dataRows.length} movie${dataRows.length > 1 ? 's' : ''}`,
    );
  } catch (error) {
    console.error('Error parsing tab data:', error);
    toast.error('Failed to parse tab-separated data');
  }
};

export const handleFullCSVPaste = async (
  csvData: string,
  startRowIndex: number = 0,
  tableData: TableRow[],
  setTableData: (data: TableRow[]) => void,
) => {
  try {
    const parsedRows = parseCSVRows(csvData);

    if (parsedRows.length === 0) {
      toast.error('No data found to paste');
      return;
    }

    const hasHeaders =
      parsedRows.length > 0 && isHeaderRow(parsedRows[0].join(','));
    const dataRows = hasHeaders ? parsedRows.slice(1) : parsedRows;

    if (dataRows.length === 0) {
      toast.error('No data rows found');
      return;
    }

    let columnMap: { [key: number]: string } = {};

    if (hasHeaders) {
      const headers = parsedRows[0];
      headers.forEach((header, index) => {
        const columnKey = getColumnKeyFromHeader(header);
        if (columnKey) {
          columnMap[index] = columnKey;
        }
      });
    } else {
      const standardColumnOrder = [
        LOAD_TABLE_FIELDS.internal_id,
        LOAD_TABLE_FIELDS.version_id,
        LOAD_TABLE_FIELDS.title,
        LOAD_TABLE_FIELDS.release_date,
        LOAD_TABLE_FIELDS.budget,
        LOAD_TABLE_FIELDS.actors,
        'actors_ids',
        LOAD_TABLE_FIELDS.directors,
        'directors_ids',
        LOAD_TABLE_FIELDS.writers,
        'writers_ids',
        LOAD_TABLE_FIELDS.producers,
        'producers_ids',
        LOAD_TABLE_FIELDS.stars,
        'stars_ids',
        LOAD_TABLE_FIELDS.plot,
        'script_id',
        LOAD_TABLE_FIELDS.mpaa_rating,
        LOAD_TABLE_FIELDS.runtime,
        LOAD_TABLE_FIELDS.aspect_ratio,
        LOAD_TABLE_FIELDS.genres,
      ];

      standardColumnOrder.forEach((columnKey, index) => {
        if (columnKey) {
          columnMap[index] = columnKey;
        }
      });
    }

    const newTableData = [...tableData];
    const allPersonIds: string[] = [];
    const rowPersonIds: {
      [rowIndex: number]: { [personType: string]: string[] };
    } = {};

    for (let i = 0; i < dataRows.length; i++) {
      const values = dataRows[i];
      const rowIndex = startRowIndex + i;

      while (newTableData.length <= rowIndex) {
        newTableData.push(createEmptyRow());
      }

      rowPersonIds[rowIndex] = {};
      let scriptId: string | null = null;
      let scriptName: string | null = null;

      const scriptIdColIndex = findScriptIdColumnIndex(columnMap);

      values.forEach((value, colIndex) => {
        const columnKey = columnMap[colIndex];
        if (!columnKey || !value) return;

        // Handle person IDs
        if (columnKey.endsWith('_ids')) {
          const personType = columnKey.replace('_ids', '');
          rowPersonIds[rowIndex][personType] = processPersonIds(
            value,
            columnKey,
          );
          allPersonIds.push(...rowPersonIds[rowIndex][personType]);
          return;
        }

        // Handle script ID
        if (columnKey === 'script_id') {
          scriptId = value.trim();
          return;
        }

        // Handle plot/script name
        if (columnKey === LOAD_TABLE_FIELDS.plot) {
          const hasScriptId =
            scriptIdColIndex !== null && values[scriptIdColIndex]?.trim();
          if (hasScriptId) {
            scriptName = value.trim();
          } else {
            newTableData[rowIndex][columnKey as keyof TableRow] = value;
          }
          return;
        }

        // Handle genres
        if (columnKey === LOAD_TABLE_FIELDS.genres) {
          newTableData[rowIndex][columnKey as keyof TableRow] =
            processGenres(value);
          return;
        }

        // Handle budget
        if (columnKey === LOAD_TABLE_FIELDS.budget) {
          newTableData[rowIndex][columnKey as keyof TableRow] =
            parseMoneyValue(value);
          return;
        }

        // Handle regular fields
        newTableData[rowIndex][columnKey as keyof TableRow] = value;
      });

      // Build script object if we have script_id
      if (scriptId) {
        setScriptData(newTableData[rowIndex], scriptId, scriptName || '');
      }

      // Mark row as exportable since it was imported
      newTableData[rowIndex].is_export_allowed = true;
    }

    if (allPersonIds.length > 0) {
      const uniquePersonIds = [...new Set(allPersonIds)];
      const personsData = await fetchPersonsDataInBatches(uniquePersonIds);

      Object.entries(rowPersonIds).forEach(([rowIndexStr, personTypes]) => {
        const rowIndex = parseInt(rowIndexStr);

        Object.entries(personTypes).forEach(([personType, ids]) => {
          const persons = ids.map((id) => personsData[id]).filter(Boolean);
          const personKey =
            LOAD_TABLE_FIELDS[personType as keyof typeof LOAD_TABLE_FIELDS];

          if (personKey) {
            newTableData[rowIndex][personKey as keyof TableRow] = persons;
          }
        });
      });
    }

    setTableData(newTableData);
    toast.success(
      `Successfully pasted ${dataRows.length} movie${dataRows.length > 1 ? 's' : ''} from CSV`,
    );
  } catch (error) {
    console.error('Error parsing CSV:', error);
    toast.error('Failed to parse CSV data');
  }
};

export const handleSmartPaste = async (
  clipboardData: string,
  startRowIndex: number = 0,
  focusedColumnKey: string | undefined,
  tableData: TableRow[],
  setTableData: (data: TableRow[]) => void,
) => {
  if (focusedColumnKey === LOAD_TABLE_FIELDS.plot) {
    await handleSingleCellPaste(
      clipboardData,
      startRowIndex,
      focusedColumnKey,
      tableData,
      setTableData,
    );
    return;
  }

  const {
    cleanedData,
    lines,
    hasMultipleColumns,
    hasMultipleRows,
    hasHeaders,
    hasTabSeparatedData,
    hasCommaSeparatedData,
    isUUIDs,
  } = detectPasteType(clipboardData);

  // console.log('Paste detection:', {
  //   hasMultipleColumns,
  //   hasMultipleRows,
  //   hasHeaders,
  //   hasTabSeparatedData,
  //   hasCommaSeparatedData,
  //   lineCount: lines.length,
  //   isUUIDs
  // });

  // Handle UUID pasting - default to Internal ID column if no column is focused
  if (isUUIDs && !hasMultipleColumns) {
    const targetColumnKey = focusedColumnKey || LOAD_TABLE_FIELDS.internal_id;

    if (hasMultipleRows) {
      // Multiple UUIDs - paste into column
      await handleSingleColumnPaste(
        lines,
        startRowIndex,
        targetColumnKey,
        tableData,
        setTableData,
      );
    } else {
      // Single UUID - paste into cell
      await handleSingleCellPaste(
        cleanedData,
        startRowIndex,
        targetColumnKey,
        tableData,
        setTableData,
      );
    }
    return;
  }

  if (hasMultipleColumns && hasMultipleRows) {
    if (hasTabSeparatedData) {
      await handleTabDataPaste(
        cleanedData,
        startRowIndex,
        tableData,
        setTableData,
      );
    } else if (hasCommaSeparatedData) {
      await handleFullCSVPaste(
        cleanedData,
        startRowIndex,
        tableData,
        setTableData,
      );
    } else {
      await handleTabDataPaste(
        cleanedData,
        startRowIndex,
        tableData,
        setTableData,
      );
    }
  } else if (!hasMultipleColumns && hasMultipleRows && focusedColumnKey) {
    await handleSingleColumnPaste(
      lines,
      startRowIndex,
      focusedColumnKey,
      tableData,
      setTableData,
    );
  } else if (hasMultipleColumns && !hasMultipleRows) {
    if (hasTabSeparatedData) {
      await handleTabDataPaste(
        cleanedData,
        startRowIndex,
        tableData,
        setTableData,
      );
    } else if (hasCommaSeparatedData) {
      await handleFullCSVPaste(
        cleanedData,
        startRowIndex,
        tableData,
        setTableData,
      );
    }
  } else if (!hasMultipleColumns && !hasMultipleRows && focusedColumnKey) {
    await handleSingleCellPaste(
      cleanedData,
      startRowIndex,
      focusedColumnKey,
      tableData,
      setTableData,
    );
  } else {
    toast.error(
      'Unable to determine paste format. Please check your data format.',
    );
  }
};
